/**
 * StreamingFlyoutContext -- Shared flyout state for all streaming subpages.
 *
 * Allows Analytics, Logs, and Settings flyout panels to open from any page
 * under /streaming/dashboard/* without navigating away from the current page.
 *
 * The actual SideFlyout rendering lives in StreamingLayout.tsx (the layout route).
 */
import { createContext, useContext, useCallback, useState, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StreamingFlyoutMode = "analytics" | "settings" | "logs" | null;

interface StreamingFlyoutContextValue {
  flyoutMode: StreamingFlyoutMode;
  /**
   * Optional zone the open flyout should expand and scroll to (e.g.
   * "media-requests"). Null means open at the top with nothing targeted.
   */
  flyoutTarget: string | null;
  openFlyout: (mode: Exclude<StreamingFlyoutMode, null>, target?: string | null) => void;
  closeFlyout: () => void;
}

// ---------------------------------------------------------------------------
// Host registry
//
// The sidebar renders above the streaming routes and cannot read this context,
// yet it needs to know whether a flyout host (StreamingLayout) is mounted: on a
// route that hosts one, a settings link opens the panel in place via a custom
// event; on a route that does not (the standalone Requests surface), the same
// link must fall through to real navigation, or the event reaches no listener
// and nothing happens.
// ---------------------------------------------------------------------------

let flyoutHostCount = 0;

/** Called by a flyout host on mount; returns its unregister for cleanup. */
export const registerStreamingFlyoutHost = (): (() => void) => {
  flyoutHostCount += 1;
  return () => {
    flyoutHostCount = Math.max(0, flyoutHostCount - 1);
  };
};

/** Whether a streaming flyout host is currently mounted anywhere in the tree. */
export const isStreamingFlyoutHostMounted = (): boolean => flyoutHostCount > 0;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const StreamingFlyoutCtx = createContext<StreamingFlyoutContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useStreamingFlyout = (): StreamingFlyoutContextValue => {
  const ctx = useContext(StreamingFlyoutCtx);
  if (!ctx) {
    throw new Error("useStreamingFlyout must be used within StreamingFlyoutProvider");
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const StreamingFlyoutProvider = ({ children }: { children: ReactNode }) => {
  const [flyoutMode, setFlyoutMode] = useState<StreamingFlyoutMode>(null);
  const [flyoutTarget, setFlyoutTarget] = useState<string | null>(null);

  const openFlyout = useCallback(
    (mode: Exclude<StreamingFlyoutMode, null>, target: string | null = null) => {
      setFlyoutMode(mode);
      setFlyoutTarget(target);
    },
    []
  );

  const closeFlyout = useCallback(() => {
    setFlyoutMode(null);
    setFlyoutTarget(null);
  }, []);

  return (
    <StreamingFlyoutCtx.Provider value={{ flyoutMode, flyoutTarget, openFlyout, closeFlyout }}>
      {children}
    </StreamingFlyoutCtx.Provider>
  );
};
